// Super Admin diagnostic: Gemini API health ping. Hits /api/admin/gemini-health
// which is cached 60s server-side, so hammering the Refresh button doesn't
// drain the key's quota. Click the pill or the button to re-ping on demand.
import { useEffect, useState } from "react";
import { CheckCircle, Warning, XCircle, ArrowClockwise } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const STATUS = {
  ok: { bg: "bg-[#F0F7F2]", text: "text-[#2F6B3A]", label: "Gemini ✓", icon: CheckCircle },
  degraded: { bg: "bg-[#FFF9E6]", text: "text-[#8A5A00]", label: "Gemini degraded", icon: Warning },
  down: { bg: "bg-[#FDF1ED]", text: "text-[#C16E5A]", label: "Gemini down", icon: XCircle },
};

export default function GeminiHealthCard({ compact = false }) {
  const [data, setData] = useState(null);
  const [checking, setChecking] = useState(false);

  const ping = async () => {
    setChecking(true);
    try {
      const r = await api.get("/admin/gemini-health");
      setData(r.data);
    } catch (e) {
      setData({ status: "down", error: e.response?.data?.detail || "Ping failed", latency_ms: 0 });
    } finally { setChecking(false); }
  };

  useEffect(() => { ping(); const id = setInterval(ping, 5 * 60 * 1000); return () => clearInterval(id); }, []);

  if (!data) return compact ? null : <div className="text-sm text-[#6B6A68]">Pinging Gemini…</div>;

  const sty = STATUS[data.status] || STATUS.degraded;
  const Icon = sty.icon;

  if (compact) {
    return (
      <button onClick={ping} disabled={checking} data-testid="gemini-health-pill"
        className={`text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${sty.bg} ${sty.text} hover:opacity-80`}
        title={`${data.status.toUpperCase()} · ${data.latency_ms}ms · model ${data.model || "gemini"}${data.error ? "\n\n" + data.error : ""}`}>
        <Icon size={10} weight="duotone" /> {sty.label}
        {data.cached && <span className="text-[9px] opacity-60">(cached)</span>}
      </button>
    );
  }

  return (
    <div className="bg-white border hairline rounded-lg p-5" data-testid="gemini-health-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-serif-display text-lg text-[#1C1B1A]">Gemini API health</h3>
          <p className="text-xs text-[#6B6A68] mt-1 max-w-xl">
            Live ping against Google Gemini using your BYOK key. Cached 60s server-side; auto-refreshes every 5 min.
            When this goes yellow or red, any Public Q&A / Refinery / Workbench run routed through Gemini may 503.
          </p>
        </div>
        <button onClick={ping} disabled={checking} data-testid="gemini-health-refresh"
          className="text-xs px-3 py-1.5 rounded-md border hairline hover:border-[#4A5D4E] flex items-center gap-1.5 disabled:opacity-50">
          <ArrowClockwise size={12} weight="bold" className={checking ? "animate-spin" : ""} />
          {checking ? "Pinging…" : "Re-ping"}
        </button>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        <Stat label="Status" value={
          <span className={`inline-flex items-center gap-1 ${sty.text}`}><Icon size={14} weight="duotone" /> {sty.label}</span>
        } />
        <Stat label="Latency" value={`${data.latency_ms || 0}ms`} />
        <Stat label="Model" value={<span className="font-mono text-xs">{data.model || "—"}</span>} />
      </div>
      {data.error && (
        <div className="mt-3 bg-[#FDF1ED] text-[#C16E5A] border border-[#C16E5A] rounded p-2.5 text-xs" data-testid="gemini-health-error">
          <strong>Error:</strong> {data.error}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border hairline rounded-md p-3">
      <div className="text-[9px] uppercase tracking-[0.18em] text-[#6B6A68]">{label}</div>
      <div className="text-sm text-[#1C1B1A] mt-1">{value}</div>
    </div>
  );
}
